'use client'

import translateText from 'app/lib/translatedText'
// import { IconNavigateBack, IconNavigateForward } from 'components/icons'
import Link from 'components/shared/link'
import { CarouselItem } from 'components/ui/carousel'
import WidgetWrapper from 'components/WidgetWrapper'
import AutoScroll from 'embla-carousel-auto-scroll'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { Carousel, CarouselContent } from 'ui/carousel'
import { fetchTeamData } from './actions/fetchCarouselData'

// type CarouselApi = ReturnType<typeof useEmblaCarousel>[1]

interface TeamWidgetProps {
    lang: string
    data: ITeamWidget
    className?: string
    isHostDomain?: boolean
    config?: IDomainConfig
    /**
     * Pre-fetched widget data from server component.
     * If provided, this widget will use it instead of making a client-side API call.
     */
    /**
     * Pre-fetched widget data from server component.
     * If provided, this widget will use it instead of making a client-side API call.
     */
    widgetData?: import('app/types/widget-data').TeamWidgetData | null
}

export default function TeamWidget({ data, lang, config, widgetData }: TeamWidgetProps) {
    const [items, setItems] = useState<ICarouselItem[]>([])
    const [loading, setLoading] = useState(true)
    const [carouselApi, setCarouselApi] = useState<ReturnType<typeof import('embla-carousel-react').default>[1] | undefined>()
    const containerRef = useRef<HTMLDivElement>(null)
    const [isInView, setIsInView] = useState(true)

    // Pause carousel auto-scroll when not in viewport to improve scroll performance
    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => setIsInView(!!entry?.isIntersecting),
            { rootMargin: '50px', threshold: 0 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!carouselApi) return
        try {
            const plugins = carouselApi.plugins()
            const pluginApi = plugins?.autoScroll as { stop?: () => void; play?: () => void } | undefined
            if (pluginApi?.stop && pluginApi?.play) {
                if (isInView) pluginApi.play()
                else pluginApi.stop()
            }
        } catch {
            /* ignore */
        }
    }, [carouselApi, isInView])

    useEffect(() => {
        // If pre-fetched widget data is available, use it instead of fetching
        if (widgetData?.items && widgetData.items.length > 0) {
            const preFetchedItems = widgetData.items.map((item) => ({
                id: item.id,
                name: item.name,
                logo: item.logo || '',
            }))
            setItems(preFetchedItems)
            setLoading(false)
            return
        }

        // Fallback to client-side fetch if no pre-fetched data available
        const getData = async () => {
            if (config) {
                try {
                    const fetchedItems = await fetchTeamData(config?.organisation_id || '')
                    setItems(fetchedItems)
                } catch (error) {
                    console.error('Error fetching carousel data:', error)
                } finally {
                    setLoading(false)
                }
            }
        }

        getData()
    }, [widgetData, config])

    if (!loading && !items.length) {
        return null
    }

    return (
        <WidgetWrapper>
            <div ref={containerRef} className='relative w-full mt-24 lg:mt-28 my-10 md:my-16 '>
                <Carousel
                    setApi={setCarouselApi}
                    plugins={[
                        AutoScroll({
                            speed: 1,
                            stopOnInteraction: true,
                            playOnInit: true,
                            direction: 'forward',
                            rootNode: emblaRoot => emblaRoot.parentElement as HTMLElement
                        })
                    ]}
                    className='w-full relative'
                    opts={{ align: 'start', loop: true, dragFree: true, containScroll: 'trimSnaps' }}
                >
                    <CarouselContent className='mx-auto'>
                        {loading
                            ? // Loading skeleton cards
                              [...Array(10)].map((_, i) => (
                                  <CarouselItem key={i} className='pl-4 basis-1/1'>
                                      <div className='relative md:h-fit md:!w-[240px] md:!min-w-[240px] !w-[170px] !min-w-[170px]'>
                                          <div className='size-[100px] md:size-[150px] glassmorphism carousel-loading-shimmer rounded-lg border-2 border-border'>
                                              <div className='relative h-[120px] md:h-[150px] rounded-t-lg bg-muted/20' />
                                              <div className='p-3 space-y-2'>
                                                  <div className='h-4 bg-muted/20 rounded' />
                                                  <div className='h-3 w-3/4 bg-muted/20 rounded' />
                                              </div>
                                          </div>
                                      </div>
                                  </CarouselItem>
                              ))
                            : // Actual content
                              items.map((item, index) => {
                                  return (
                                      <CarouselItem key={index} className='pl-4 basis-1/1'>
                                          <Link href={item.link || `/teams/${item.id}` || '#'}>
                                              <div className='relative size-[130px] md:size-[150px] grid place-items-center'>
                                                  {item.logo ? (
                                                      <Image
                                                          src={item.logo}
                                                          fill
                                                          alt={item.name || 'carousel image'}
                                                          className='object-contain z-10 rounded-t-lg'
                                                          sizes="(max-width: 768px) 130px, 150px"
                                                      />
                                                  ) : item.name ? (
                                                      <p className='text-5xl italic mx-5 font-extrabold text-center'>
                                                          {item.name.charAt(0).toUpperCase()}
                                                      </p>
                                                  ) : null}
                                              </div>
                                          </Link>
                                      </CarouselItem>
                                  )
                              })}
                    </CarouselContent>
                    <div className='absolute -top-12 md:-top-[52px] inset-x-0 mx-auto w-fit flex justify-between items-center'>
                        {data?.title?.tr?.[0]?.text && (
                            <p className='text-base md:text-xl lg:text-2xl font-bold text-center'>
                                {translateText(lang, data.title)}
                            </p>
                        )}
                    </div>
                    {/* <div className='absolute bottom-1/2 bg-green-400 w-full flex justify-between items-center'>
                        <div className='absolute inset-0  md:w-[90%] bg-yellow-400 w-[90%] max-w-[1400px] flex items-center gap-2'>
                            <CarouselPrevious
                                onClick={scrollPrev}
                                className='h-7 w-7 md:h-10 md:w-10 ml-5 md:ml-0 flex border rounded hover:!bg-[rgba(70,118,216)]/[.24] bg-transparent backdrop-blur-md absolute inset-0 mr-auto'
                                icon={<IconNavigateBack />}
                            />
                            <CarouselNext
                                onClick={scrollNext}
                                className='h-7 w-7 md:h-10 md:w-10 flex border rounded hover:!bg-[rgba(70,118,216)]/[.24] bg-transparent backdrop-blur-md absolute inset-r-0'
                                icon={<IconNavigateForward />}
                            />
                        </div>
                    </div> */}
                </Carousel>
            </div>
        </WidgetWrapper>
    )
}
